import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Messaging from '../components/Messaging/Messaging';
import './Dashboard.css';
import './AdminDashboard.css';

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'pending', label: 'Pending' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const ADMIN_TABS = ['Dashboard', 'Applicants', 'Messages'];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get('/users/all');
      setUsers(res.data);
    } catch {
      // handled
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadUnread();
  }, [loadUsers]);

  const loadUnread = async () => {
    try {
      const res = await api.get('/messages/unread-count');
      setUnreadCount(res.data.count);
    } catch { }
  };

  const loadUserDetail = async (u) => {
    setSelectedUser(u);
    setDetailLoading(true);
    setStatusUpdate(u.application_status || 'not_started');
    try {
      const res = await api.get(`/users/${u.id}`);
      setDetailUser(res.data);
    } catch {
      setDetailUser(u);
    } finally {
      setDetailLoading(false);
    }
  };

  const updateStatus = async () => {
    try {
      await api.put(`/users/${selectedUser.id}/status`, { application_status: statusUpdate });
      setStatusMsg('Status updated successfully.');
      loadUsers();
      setTimeout(() => setStatusMsg(''), 3000);
    } catch {
      setStatusMsg('Failed to update status.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openMessageTo = (u) => {
    setMessageTarget({ ...u });   // spread to force re-render if same user clicked twice
    setActiveTab('Messages');
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const name = `${u.first_name} ${u.last_name}`.toLowerCase();
    const matchSearch = !search || name.includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || u.application_status === statusFilter || (!u.application_status && statusFilter === 'not_started');
    return matchSearch && matchStatus;
  });

  // Stats
  const stats = {
    total: users.length,
    pending: users.filter(u => u.application_status === 'pending').length,
    under_review: users.filter(u => u.application_status === 'under_review').length,
    approved: users.filter(u => u.application_status === 'approved').length,
  };

  const getBadgeClass = (status) => {
    const map = { pending: 'pending', under_review: 'review', approved: 'approved', rejected: 'rejected' };
    return `badge badge-${map[status] || 'default'}`;
  };

  return (
    <div className="dash-page-wrapper">
      {/* Dashboard Top Bar */}
      <div className="dash-topbar">
        <Link to="/" className="dash-topbar-home">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18">
            <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          Home
        </Link>
        <span className="dash-topbar-title">Gold Card Portal — Admin</span>
        <div className="dash-topbar-user">
          Signed in as <strong>{user.first_name} {user.last_name}</strong>
          <span className="dash-topbar-role">Administrator</span>
        </div>
      </div>

      <main className="dashboard-main">
        <div className="dashboard-layout">
          {/* Admin Sidebar */}
          <aside className="dash-sidebar">
            <div className="dash-user-card">
              <div className="dash-avatar">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </div>
              <h3>{user.first_name} {user.last_name}</h3>
              <p className="dash-email">USCIS Administrator</p>
              <span className="badge" style={{ background: 'rgba(201,162,39,0.25)', color: '#f0c84a', border: '1px solid #f0c84a' }}>Admin</span>
            </div>
            <nav className="dash-nav">
              {ADMIN_TABS.map(tab => (
                <button
                  key={tab}
                  className={`dash-nav-item ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'Dashboard' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>}
                  {tab === 'Applicants' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>}
                  {tab === 'Messages' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>}
                  <span>{tab}</span>
                  {tab === 'Messages' && unreadCount > 0 && (
                    <span className="nav-badge">{unreadCount}</span>
                  )}
                </button>
              ))}
            </nav>
            <button className="dash-logout-btn" onClick={handleLogout}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              Sign Out
            </button>
          </aside>

          {/* Main content */}
          <div className="dash-content">

            {/* ── ADMIN OVERVIEW ── */}
            {activeTab === 'Dashboard' && (
              <div className="dash-panel">
                <div className="dash-panel-header">
                  <h2>Control Panel</h2>
                  <p>Overview of all Gold Card applications and system activity.</p>
                </div>

                <div className="stats-grid admin-stats">
                  <div className="stat-card">
                    <div className="stat-icon stat-icon-blue">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                      </svg>
                    </div>
                    <div>
                      <div className="stat-label">Total Applicants</div>
                      <div className="stat-value admin-stat-num">{stats.total}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon stat-icon-gold">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div>
                      <div className="stat-label">Pending</div>
                      <div className="stat-value admin-stat-num">{stats.pending}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon stat-icon-navy">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </div>
                    <div>
                      <div className="stat-label">Under Review</div>
                      <div className="stat-value admin-stat-num">{stats.under_review}</div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon stat-icon-green">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <div>
                      <div className="stat-label">Approved</div>
                      <div className="stat-value admin-stat-num">{stats.approved}</div>
                    </div>
                  </div>
                </div>

                {/* Recent applicants preview */}
                <h4 className="admin-section-sub">Recent Applicants</h4>
                <div className="admin-table-wrapper">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Card Type</th>
                        <th>Status</th>
                        <th>Registered</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.slice(0, 5).map(u => (
                        <tr key={u.id}>
                          <td><strong>{u.first_name} {u.last_name}</strong></td>
                          <td className="td-muted">{u.email}</td>
                          <td>{u.card_type || '—'}</td>
                          <td><span className={getBadgeClass(u.application_status)}>{u.application_status?.replace('_', ' ') || 'Not Started'}</span></td>
                          <td className="td-muted">{new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                          <td>
                            <button className="admin-action-btn" onClick={() => { loadUserDetail(u); setActiveTab('Applicants'); }}>
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr><td colSpan={6} className="td-empty">No applicants registered yet.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── APPLICANTS TAB ── */}
            {activeTab === 'Applicants' && (
              <div className={`admin-applicants-layout ${selectedUser ? 'has-detail' : ''}`}>
                {/* User List */}
                <div className="dash-panel admin-user-list">
                  <div className="dash-panel-header">
                    <h2>All Applicants</h2>
                    <p>{filteredUsers.length} of {users.length} applicants</p>
                  </div>

                  {/* Filters */}
                  <div className="admin-filters">
                    <input
                      type="text"
                      className="form-input admin-search"
                      placeholder="Search by name or email…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                    <select className="form-select admin-filter-select"
                      value={statusFilter}
                      onChange={e => setStatusFilter(e.target.value)}>
                      <option value="all">All Statuses</option>
                      {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>

                  {loading ? (
                    <div className="spinner" />
                  ) : (
                    <div className="admin-table-wrapper">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Applicant</th>
                            <th>Card Type</th>
                            <th>Status</th>
                            <th>Submitted</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.map(u => (
                            <tr
                              key={u.id}
                              className={selectedUser?.id === u.id ? 'row-selected' : ''}
                              onClick={() => loadUserDetail(u)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>
                                <div className="td-user">
                                  <div className="td-avatar">
                                    {u.first_name?.[0]}{u.last_name?.[0]}
                                  </div>
                                  <div>
                                    <strong>{u.first_name} {u.last_name}</strong>
                                    <div className="td-email">{u.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td>{u.card_type || '—'}</td>
                              <td><span className={getBadgeClass(u.application_status)}>{u.application_status?.replace('_', ' ') || 'Not Started'}</span></td>
                              <td className="td-muted">
                                {u.submitted_at ? new Date(u.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                              </td>
                              <td>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" style={{ color: 'var(--gray)' }}>
                                  <path d="M9 18l6-6-6-6" />
                                </svg>
                              </td>
                            </tr>
                          ))}
                          {filteredUsers.length === 0 && (
                            <tr><td colSpan={5} className="td-empty">No applicants found.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* User Detail Panel */}
                {selectedUser && (
                  <div className="dash-panel admin-user-detail">
                    <div className="detail-header">
                      <div className="detail-avatar">
                        {selectedUser.first_name?.[0]}{selectedUser.last_name?.[0]}
                      </div>
                      <div>
                        <h3>{selectedUser.first_name} {selectedUser.last_name}</h3>
                        <p>{selectedUser.email}</p>
                      </div>
                      <button className="icon-close" onClick={() => setSelectedUser(null)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>

                    {detailLoading ? <div className="spinner" /> : (
                      <>
                        <div className="detail-section">
                          <div className="detail-label">Phone</div>
                          <div className="detail-value">{detailUser?.phone || '—'}</div>
                        </div>
                        <div className="detail-section">
                          <div className="detail-label">Date of Birth</div>
                          <div className="detail-value">{detailUser?.date_of_birth ? new Date(detailUser.date_of_birth).toLocaleDateString() : '—'}</div>
                        </div>
                        <div className="detail-section">
                          <div className="detail-label">Country of Birth</div>
                          <div className="detail-value">{detailUser?.country_of_birth || '—'}</div>
                        </div>
                        <div className="detail-section">
                          <div className="detail-label">Country of Citizenship</div>
                          <div className="detail-value">{detailUser?.country_of_citizenship || '—'}</div>
                        </div>
                        <div className="detail-section">
                          <div className="detail-label">A-Number</div>
                          <div className="detail-value">{detailUser?.a_number || '—'}</div>
                        </div>
                        <div className="detail-section">
                          <div className="detail-label">Card Type</div>
                          <div className="detail-value">{detailUser?.card_type || '—'}</div>
                        </div>
                        <div className="detail-section">
                          <div className="detail-label">Family Members</div>
                          <div className="detail-value">{detailUser?.family_members_count ?? '—'}</div>
                        </div>

                        <hr className="divider" />

                        {/* Status update */}
                        <div className="detail-status-update">
                          <label className="form-label">Update Application Status</label>
                          <div className="status-update-row">
                            <select
                              className="form-select"
                              value={statusUpdate}
                              onChange={e => setStatusUpdate(e.target.value)}
                            >
                              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                            </select>
                            <button className="btn btn-primary" onClick={updateStatus}>Save</button>
                          </div>
                          {statusMsg && (
                            <p className={`status-save-msg ${statusMsg.includes('Failed') ? 'error' : 'success'}`}>
                              {statusMsg}
                            </p>
                          )}
                        </div>

                        <hr className="divider" />

                        {/* Message button */}
                        <button
                          className="btn btn-primary admin-msg-btn"
                          onClick={() => openMessageTo(selectedUser)}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16">
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                          </svg>
                          Send Message to Applicant
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── MESSAGES TAB ── */}
            {activeTab === 'Messages' && (
              <div className="dash-panel">
                <div className="dash-panel-header">
                  <h2>Applicant Communications</h2>
                  <p>Send and receive messages with Gold Card applicants.</p>
                </div>
                <Messaging
                  key={messageTarget?.id || 'admin-messages'}
                  initialTarget={messageTarget}
                />
              </div>
            )}

          </div>
        </div>
      </main>
    </div>
  );
}